import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  MailCheck,
  MailWarning,
  Send,
  ShieldCheck,
  KeyRound,
  Pencil,
  Printer,
  Shirt,
  ShieldOff,
} from 'lucide-react';
import {
  fetchBureauMembres,
  fetchAnnuaireMembres,
  createBureauMembre,
  deleteBureauMembre,
  updateBureauMembre,
  renvoyerInvitationMembre,
  reinitialiserMotDePasseMembre,
  retirer2faMembre,
  fetchRolesBureau,
  fetchBureauBenevoles,
  fetchReferentielBenevole,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { usePersonnalisation } from '../hooks/usePersonnalisation';
import { imprimerListe } from '../utils/impression';
import RoleBadge from '../components/RoleBadge';
import { nomFamille, prenomUsage } from '../utils/nom';

import { dateFr } from '../utils/dates';
const formMembreInitial = {
  prenom: '',
  nom: '',
  email: '',
  telephone: '',
  type: 'benevole' as 'benevole' | 'bureau',
  bureauRoleId: '',
};

export default function BureauMembresPage() {
  const { user, isLoading: authLoading, aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const [filtre, setFiltre] = useState<'tous' | 'bureau' | 'benevole' | 'tailles'>(() => {
    const demande = params.get('filtre');
    return demande === 'bureau' || demande === 'benevole' || demande === 'tailles' ? demande : 'tous';
  });
  const [showFormMembre, setShowFormMembre] = useState(false);
  const [formMembre, setFormMembre] = useState(formMembreInitial);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [membreEnEdition, setMembreEnEdition] = useState<any | null>(null);
  const [formEdition, setFormEdition] = useState({ prenom: '', nom: '', email: '', telephone: '', photoUrl: '', anonyme: false, bureauRoleId: '' });

  // Sur la grille des droits, et non sur le seul role : c'est la fonction
  // au bureau qui decide desormais de l'acces a cet ecran, et le serveur
  // refusera l'appel si elle ne le prevoit pas.
  const isBureau = aLeDroit('voir_benevoles');
  // L'admin (super admin ou president) peut reinitialiser le mot de passe des membres.
  const estAdmin = user?.roles?.includes('ROLE_ADMIN') || user?.roles?.includes('ROLE_SUPER_ADMIN');
  // Le support, entré par la console : sa session vient d'un lien signé, il
  // n'a pas de mot de passe à resaisir — celui du compte technique est jeté.
  const estSuperAdmin = user?.roles?.includes('ROLE_SUPER_ADMIN') === true;
  // Un benevole accede a l'annuaire, mais sans aucune action de gestion.
  const isBenevole = user?.roles?.includes('ROLE_VOLUNTEER') || isBureau;
  const lectureSeule = !isBureau;

  const membresQuery = useQuery({
    queryKey: ['membres', lectureSeule ? 'annuaire' : 'bureau'],
    queryFn: () => (lectureSeule ? fetchAnnuaireMembres() : fetchBureauMembres()),
    enabled: isBenevole,
  });

  const rolesBureauQuery = useQuery({
    queryKey: ['roles-bureau'],
    queryFn: fetchRolesBureau,
    enabled: isBureau,
  });

  const rafraichirMembres = () => {
    queryClient.invalidateQueries({ queryKey: ['membres', 'bureau'] });
    queryClient.invalidateQueries({ queryKey: ['membres', 'annuaire'] });
    queryClient.invalidateQueries({ queryKey: ['bureau-membres'] });
  };

  const createMembreMutation = useMutation({
    mutationFn: () =>
      createBureauMembre({
        prenom: formMembre.prenom,
        nom: formMembre.nom,
        email: formMembre.email,
        telephone: formMembre.telephone || undefined,
        type: formMembre.type,
        bureauRoleId: formMembre.type === 'bureau' && formMembre.bureauRoleId ? Number(formMembre.bureauRoleId) : null,
      }),
    onSuccess: (data) => {
      rafraichirMembres();
      setShowFormMembre(false);
      setFormMembre(formMembreInitial);
      // L'email peut échouer sans annuler la création : on le signale explicitement.
      setNotice({ type: data.emailEnvoye ? 'success' : 'error', message: data.message });
    },
    onError: (error: Error) => setNotice({ type: 'error', message: error.message }),
  });

  const renvoyerInvitationMutation = useMutation({
    mutationFn: renvoyerInvitationMembre,
    onSuccess: (data) => {
      rafraichirMembres();
      setNotice({ type: 'success', message: data.message });
    },
    onError: (error: Error) => setNotice({ type: 'error', message: error.message }),
  });

  const resetMdpMutation = useMutation({
    mutationFn: reinitialiserMotDePasseMembre,
    onSuccess: (data) => {
      setNotice({ type: 'success', message: data.message });
    },
    onError: (error: Error) => setNotice({ type: 'error', message: error.message }),
  });

  // Le dépannage du président qui a changé de téléphone : l'administrateur
  // retire la protection, contre son propre mot de passe.
  const retirer2faMutation = useMutation({
    mutationFn: ({ id, motDePasse }: { id: number; motDePasse: string }) =>
      retirer2faMembre(id, motDePasse),
    onSuccess: (data: any) => setNotice({ type: 'success', message: data.message }),
    onError: (error: Error) => setNotice({ type: 'error', message: error.message }),
  });

  const deleteMembreMutation = useMutation({
    mutationFn: deleteBureauMembre,
    onSuccess: () => {
      rafraichirMembres();
      setNotice(null);
    },
    onError: (error: Error) => setNotice({ type: 'error', message: error.message }),
  });

  const updateMembreMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, any> }) => updateBureauMembre(id, payload),
    onSuccess: () => {
      rafraichirMembres();
      setMembreEnEdition(null);
      setNotice({ type: 'success', message: 'Profil mis à jour.' });
    },
    onError: (error: Error) => setNotice({ type: 'error', message: error.message }),
  });

  if (authLoading || membresQuery.isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!isBenevole) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">Cette page est réservée aux bénévoles et aux membres du bureau.</p>
          </div>
        </div>
      </div>
    );
  }

  const membres = membresQuery.data?.membres ?? [];
  const rolesBureau = rolesBureauQuery.data?.rolesBureau ?? [];

  const membresFiltres = membres.filter((m: any) => filtre === 'tous' || m.type === filtre);

  const formInvalide =
    !formMembre.prenom.trim() ||
    !formMembre.nom.trim() ||
    !formMembre.email.trim() ||
    // La fonction au bureau est obligatoire : c'est elle qui porte les droits.
    (formMembre.type === 'bureau' && !formMembre.bureauRoleId);

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <Users className="w-7 h-7 text-primary-700" />
        <div>
          {/* « Membres » et non « Bénévoles » : l'écran porte les deux
              populations, et les onglets ci-dessous font la distinction. */}
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Membres</h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            {lectureSeule
              ? 'Annuaire des membres du comité.'
              : 'Créez les comptes : chaque personne reçoit un email pour définir son mot de passe.'}
          </p>
        </div>
      </div>

      {notice && (
        <div
          className={`card flex items-start gap-3 text-sm ${
            notice.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {notice.type === 'success' ? <MailCheck className="w-5 h-5 mt-0.5 shrink-0" /> : <MailWarning className="w-5 h-5 mt-0.5 shrink-0" />}
          <p className="flex-1">{notice.message}</p>
          <button onClick={() => setNotice(null)} className="text-xs underline">Fermer</button>
        </div>
      )}

      <>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {([
                { value: 'tous', label: 'Tous' },
                { value: 'bureau', label: 'Bureau' },
                { value: 'benevole', label: 'Bénévoles' },
                // La commande de textiles : les tailles déclarées sur les
                // fiches, comptées par vêtement, prêtes pour le fournisseur.
                { value: 'tailles', label: 'Tailles' },
              ] as const).map((onglet) => (
                <button
                  key={onglet.value}
                  onClick={() => setFiltre(onglet.value)}
                  className={`px-3 py-1.5 text-sm rounded-lg font-medium transition ${
                    filtre === onglet.value ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:bg-gray-700'
                  }`}
                >
                  {onglet.label}
                </button>
              ))}
            </div>
            {!lectureSeule && (
              <button
                onClick={() => setShowFormMembre(!showFormMembre)}
                className="btn-primary inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Ajouter un membre
              </button>
            )}
          </div>

          {showFormMembre && !lectureSeule && (
            <div className="card lg:p-5 space-y-3">
              <h2 className="font-semibold">Nouveau membre</h2>

              <div className="flex flex-wrap gap-2">
                {([
                  { value: 'benevole', label: 'Bénévole' },
                  { value: 'bureau', label: 'Membre du bureau' },
                ] as const).map((choix) => (
                  <button
                    key={choix.value}
                    type="button"
                    onClick={() => setFormMembre({ ...formMembre, type: choix.value, bureauRoleId: '' })}
                    className={`px-4 py-2 text-sm rounded-lg border font-medium transition ${
                      formMembre.type === choix.value
                        ? 'border-primary-600 bg-primary-50 text-primary-700'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:text-gray-400'
                    }`}
                  >
                    {choix.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <input
                  className="input"
                  placeholder="Prénom"
                  value={formMembre.prenom}
                  onChange={(e) => setFormMembre({ ...formMembre, prenom: prenomUsage(e.target.value) })}
                />
                <input
                  className="input"
                  placeholder="Nom"
                  value={formMembre.nom}
                  onChange={(e) => setFormMembre({ ...formMembre, nom: nomFamille(e.target.value) })}
                />
                <input
                  className="input"
                  type="email"
                  placeholder="Email"
                  value={formMembre.email}
                  onChange={(e) => setFormMembre({ ...formMembre, email: e.target.value })}
                />
                <input
                  className="input"
                  placeholder="Téléphone"
                  value={formMembre.telephone}
                  onChange={(e) => setFormMembre({ ...formMembre, telephone: e.target.value })}
                />
                {formMembre.type === 'bureau' && (
                  <select
                    className="input lg:col-span-2"
                    required
                    value={formMembre.bureauRoleId}
                    onChange={(e) => setFormMembre({ ...formMembre, bureauRoleId: e.target.value })}
                  >
                    {/* Obligatoire : la fonction porte les droits du membre. */}
                    <option value="">Fonction au bureau (obligatoire)…</option>
                    {rolesBureau.map((r: any) => (
                      <option key={r.id} value={r.id}>{r.label}</option>
                    ))}
                  </select>
                )}
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400">
                L'email de la personne est son identifiant de connexion. Elle recevra un lien valable 7 jours
                pour choisir son mot de passe.
              </p>

              <div className="flex gap-2">
                <button
                  onClick={() => createMembreMutation.mutate()}
                  disabled={createMembreMutation.isPending || formInvalide}
                  className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                >
                  {createMembreMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  Créer et envoyer l'invitation
                </button>
                <button onClick={() => setShowFormMembre(false)} className="text-sm text-gray-500 dark:text-gray-400">Annuler</button>
              </div>
            </div>
          )}

          {filtre === 'tailles' ? (
            <OngletTailles />
          ) : (
          <div className="grid grid-cols-1 gap-3">
            {membresFiltres.length === 0 ? (
              <p className="text-gray-500 text-sm dark:text-gray-400">Aucun membre enregistré.</p>
            ) : (
              membresFiltres.map((m: any) => (
                <div key={m.id} className="card p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                  <div className="text-sm space-y-1">
                    <p className="font-semibold text-gray-900 flex flex-wrap items-center gap-2 dark:text-gray-100">
                      {m.fullName}
                      <RoleBadge roles={m.roles ?? []} size="sm" />
                      {m.bureauRole?.label && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:text-gray-300 dark:bg-gray-800">
                          <ShieldCheck className="w-3 h-3" />
                          {m.bureauRole.label}
                        </span>
                      )}
                    </p>
                    <p className="text-gray-600 dark:text-gray-400">{m.email}</p>
                    <p className="text-gray-500 dark:text-gray-400">{m.telephone ?? 'Pas de téléphone'}</p>
                    {!lectureSeule && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {m.roles?.includes('ROLE_SUPER_ADMIN') ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-800">Compte super admin</span>
                      ) : m.motDePasseDefini ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">Compte activé</span>
                      ) : m.invitationEnCours ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                          Invitation envoyée — expire le {dateFr(m.invitationExpireAt)}
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">Invitation expirée</span>
                      )}
                      {!m.isActive && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 dark:text-gray-300">Compte désactivé</span>
                      )}
                    </div>
                    )}
                  </div>

                  {!lectureSeule && (
                  <div className="flex items-center gap-2 shrink-0">
                    {estAdmin && (
                      <button
                        onClick={() => {
                          setFormEdition({
                            prenom: m.prenom ?? '',
                            nom: m.nom ?? '',
                            email: m.email ?? '',
                            telephone: m.telephone ?? '',
                            photoUrl: m.photoUrl ?? '',
                            anonyme: m.anonyme ?? false,
                            bureauRoleId: m.bureauRole?.id ? String(m.bureauRole.id) : '',
                          });
                          setMembreEnEdition(m);
                        }}
                        title="Modifier le profil"
                        className="p-2 rounded-lg text-gray-600 hover:text-primary-700 hover:bg-primary-50 dark:text-gray-400"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {!m.motDePasseDefini && !m.roles?.includes('ROLE_SUPER_ADMIN') && (
                      <button
                        onClick={() => renvoyerInvitationMutation.mutate(m.id)}
                        disabled={renvoyerInvitationMutation.isPending}
                        title="Renvoyer l'invitation"
                        className="p-2 rounded-lg text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    )}
                    {estAdmin && m.id !== user?.id && (
                      <button
                        onClick={() => {
                          if (window.confirm(`Envoyer un lien de réinitialisation de mot de passe à ${m.fullName} ?`)) {
                            resetMdpMutation.mutate(m.id);
                          }
                        }}
                        disabled={resetMdpMutation.isPending}
                        title="Réinitialiser le mot de passe"
                        className="p-2 rounded-lg text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                    )}
                    {estAdmin && m.id !== user?.id && (
                      <button
                        onClick={() => {
                          // Le support entré par la console n'a pas de mot de
                          // passe à saisir : sa session vient d'un lien signé.
                          if (estSuperAdmin) {
                            if (window.confirm(`Retirer la connexion en deux étapes de ${m.fullName} ? La personne se reconnectera avec son seul mot de passe.`)) {
                              retirer2faMutation.mutate({ id: m.id, motDePasse: '' });
                            }
                            return;
                          }
                          const motDePasse = window.prompt(
                            `Retirer la connexion en deux étapes de ${m.fullName} ? La personne se reconnectera avec son seul mot de passe.\n\nConfirmez avec VOTRE mot de passe :`
                          );
                          if (motDePasse) {
                            retirer2faMutation.mutate({ id: m.id, motDePasse });
                          }
                        }}
                        disabled={retirer2faMutation.isPending}
                        title="Retirer la connexion en deux étapes (téléphone perdu)"
                        className="p-2 rounded-lg text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                      >
                        <ShieldOff className="w-4 h-4" />
                      </button>
                    )}
                    {m.id !== user?.id && (
                      <button
                        onClick={() => {
                          if (window.confirm(`Supprimer définitivement le compte de ${m.fullName} ?`)) {
                            deleteMembreMutation.mutate(m.id);
                          }
                        }}
                        disabled={deleteMembreMutation.isPending}
                        title="Supprimer le compte"
                        className="p-2 rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  )}
                </div>
              ))
            )}
          </div>
          )}
        </div>

        {membreEnEdition && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
            <div className="card w-full max-w-md p-5 space-y-3">
              <h2 className="font-semibold text-lg">Modifier le profil</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">{membreEnEdition.fullName}</p>
              <input
                className="input"
                placeholder="Prénom"
                value={formEdition.prenom}
                onChange={(e) => setFormEdition({ ...formEdition, prenom: prenomUsage(e.target.value) })}
              />
              <input
                className="input"
                placeholder="Nom"
                value={formEdition.nom}
                onChange={(e) => setFormEdition({ ...formEdition, nom: nomFamille(e.target.value) })}
              />
              <input
                className="input"
                type="email"
                placeholder="Email"
                value={formEdition.email}
                onChange={(e) => setFormEdition({ ...formEdition, email: e.target.value })}
              />
              <input
                className="input"
                placeholder="Téléphone"
                value={formEdition.telephone}
                onChange={(e) => setFormEdition({ ...formEdition, telephone: e.target.value })}
              />
              <input
                className="input"
                type="url"
                placeholder="URL de la photo de profil"
                value={formEdition.photoUrl}
                onChange={(e) => setFormEdition({ ...formEdition, photoUrl: e.target.value })}
              />
              {membreEnEdition.type === 'bureau' && (
                <label className="block text-sm text-gray-700 dark:text-gray-300">
                  Fonction au bureau (obligatoire — elle porte les droits du membre)
                  <select
                    className="input mt-1"
                    required
                    value={formEdition.bureauRoleId}
                    onChange={(e) => setFormEdition({ ...formEdition, bureauRoleId: e.target.value })}
                  >
                    <option value="">Choisir une fonction…</option>
                    {rolesBureau.map((r: any) => (
                      <option key={r.id} value={r.id}>{r.label}</option>
                    ))}
                  </select>
                </label>
              )}
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={formEdition.anonyme}
                  onChange={(e) => setFormEdition({ ...formEdition, anonyme: e.target.checked })}
                  className="mt-0.5"
                />
                Ne pas apparaître sur la page Association
              </label>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() =>
                    updateMembreMutation.mutate({
                      id: membreEnEdition.id,
                      payload: {
                        prenom: formEdition.prenom,
                        nom: formEdition.nom,
                        email: formEdition.email,
                        telephone: formEdition.telephone || null,
                        photoUrl: formEdition.photoUrl || null,
                        anonyme: formEdition.anonyme,
                        ...(membreEnEdition.type === 'bureau'
                          ? { bureauRoleId: Number(formEdition.bureauRoleId) }
                          : {}),
                      },
                    })
                  }
                  disabled={
                    updateMembreMutation.isPending ||
                    (membreEnEdition.type === 'bureau' && !formEdition.bureauRoleId)
                  }
                  className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                >
                  {updateMembreMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  Enregistrer
                </button>
                <button onClick={() => setMembreEnEdition(null)} className="text-sm text-gray-500 dark:text-gray-400">Annuler</button>
              </div>
            </div>
          </div>
        )}
      </>
    </div>
  );
}

/**
 * L'onglet Tailles : la commande de textiles, prête pour le fournisseur.
 *
 * Chaque bénévole déclare ses tailles sur sa fiche ; ici elles se comptent
 * par type de vêtement — sous-total par type, total global de pièces — dans
 * l'ordre du référentiel, et la liste s'imprime en PDF pour partir en
 * pièce jointe au fournisseur.
 */
function OngletTailles() {
  const perso = usePersonnalisation();
  const benevolesQuery = useQuery({ queryKey: ['bureau-benevoles'], queryFn: fetchBureauBenevoles });
  const referentielQuery = useQuery({ queryKey: ['benevole-referentiel'], queryFn: fetchReferentielBenevole });

  if (benevolesQuery.isPending || referentielQuery.isPending) {
    return (
      <div className="card p-8 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const benevoles: { fullName?: string; tailles?: Record<string, string> }[] =
    benevolesQuery.data?.benevoles ?? [];
  const vetements: { code: string; label: string; tailles: string[] }[] =
    referentielQuery.data?.vetements ?? [];

  // Comptage : par type de vêtement, combien de chaque taille.
  const types = vetements
    .map((vetement) => {
      const parTaille = new Map<string, number>();
      for (const b of benevoles) {
        const taille = b.tailles?.[vetement.code];
        if (taille) {
          parTaille.set(taille, (parTaille.get(taille) ?? 0) + 1);
        }
      }
      const lignes = vetement.tailles
        .filter((t) => parTaille.has(t))
        .map((t) => ({ taille: t, nb: parTaille.get(t)! }));

      return {
        code: vetement.code,
        label: vetement.label,
        lignes,
        sousTotal: lignes.reduce((s, l) => s + l.nb, 0),
      };
    })
    .filter((type) => type.sousTotal > 0);

  const totalGlobal = types.reduce((s, t) => s + t.sousTotal, 0);
  const sansTaille = benevoles.filter((b) => !b.tailles || Object.keys(b.tailles).length === 0).length;

  const imprimer = () => {
    imprimerListe({
      titre: 'Commande de textiles',
      association: perso?.nomAssociation ?? undefined,
      sousTitre: `Tailles déclarées par ${benevoles.length} fiche(s) bénévole — sous-totaux par type de vêtement.`,
      totaux: [
        { libelle: 'pièce(s) à commander au total', valeur: String(totalGlobal) },
        ...(sansTaille > 0 ? [{ libelle: 'fiche(s) sans taille déclarée', valeur: String(sansTaille) }] : []),
      ],
      colonnes: [
        { titre: 'Vêtement', valeur: (l: any) => l.type },
        { titre: 'Taille', valeur: (l: any) => l.taille, alignement: 'centre' },
        { titre: 'Quantité', valeur: (l: any) => String(l.nb), alignement: 'centre' },
      ],
      lignes: types.flatMap((type) => [
        ...type.lignes.map((l) => ({ type: type.label, taille: l.taille, nb: l.nb })),
        { type: `${type.label} — sous-total`, taille: '', nb: type.sousTotal },
      ]),
      colonnePointage: 'Commandé',
    });
  };

  return (
    <div className="space-y-4">
      <div className="card flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Shirt className="w-6 h-6 text-primary-700 shrink-0" />
          <div>
            <p className="font-semibold text-gray-900 dark:text-gray-100">
              {totalGlobal} pièce(s) à commander
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              D'après les tailles déclarées sur les fiches bénévoles
              {sansTaille > 0 && <> — {sansTaille} fiche(s) sans taille : relancez-les avant de commander</>}.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* La deuxième page : à qui vont les pièces, ligne par bénévole. */}
          <Link to="/bureau/membres/tailles" className="btn-primary inline-flex items-center gap-2 text-sm px-4 py-2">
            <Users className="w-4 h-4" /> Vêtements par personne
          </Link>
          {totalGlobal > 0 && (
            <button onClick={imprimer} className="btn-outline inline-flex items-center gap-2 text-sm px-4 py-2">
              <Printer className="w-4 h-4" /> Exporter en PDF
            </button>
          )}
        </div>
      </div>

      {types.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Aucune taille déclarée pour le moment : chaque bénévole renseigne les siennes depuis « Mes
          informations ».
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {types.map((type) => (
            <div key={type.code} className="card p-0 overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 flex items-center justify-between">
                <p className="font-semibold text-gray-900 dark:text-gray-100">{type.label}</p>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200">
                  {type.sousTotal} pièce(s)
                </span>
              </div>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {type.lignes.map((l) => (
                    <tr key={l.taille}>
                      <td className="px-4 py-2 font-medium text-gray-700 dark:text-gray-300">{l.taille}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold text-gray-900 dark:text-gray-100">
                        {l.nb}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
